import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  computeKpis,
  computeStatusDistribution,
  computeSummaryFromItems,
  filterItemsClient,
  normalizeReportResponse,
  safeMsg,
  toRatingOver5,
} from "./report.utils";
import { fetchAppraisalsReport, finalizeAppraisalActions } from "../../../services/performanceReportApi";

const buildQueryParams = ({ filters, search, paginationModel, sortModel }) => {
  const sort = Array.isArray(sortModel) && sortModel.length > 0 ? sortModel[0] : null;

  return {
    from: filters?.from || undefined,
    to: filters?.to || undefined,
    cycle_id: filters?.cycle_id || undefined,
    department_id: filters?.department_id || undefined,
    project_id: filters?.project_id || undefined,
    status: filters?.status || undefined,
    band: filters?.band || undefined,
    recommended_action: filters?.recommended_action || undefined,
    recommendation_status: filters?.recommendation_status || undefined,
    delta_direction: filters?.delta_direction || undefined,
    q: (search || "").trim() || undefined,
    page: Number(paginationModel?.page || 0) + 1,
    per_page: Number(paginationModel?.pageSize || 25),
    sort_by: sort?.field || undefined,
    sort_dir: sort?.sort || undefined,
  };
};

export function useAppraisalsReport({ filters, search, paginationModel, sortModel }) {
  const queryClient = useQueryClient();
  const [debouncedSearch, setDebouncedSearch] = useState(search || "");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search || ""), 350);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = useMemo(
    () =>
      buildQueryParams({
        filters,
        search: debouncedSearch,
        paginationModel,
        sortModel,
      }),
    [filters, debouncedSearch, paginationModel, sortModel]
  );

  const reportQuery = useQuery({
    queryKey: ["appraisals-report", queryParams],
    queryFn: () => fetchAppraisalsReport(queryParams),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const normalized = useMemo(
    () => normalizeReportResponse(reportQuery.data || {}),
    [reportQuery.data]
  );

  const locallyFilteredItems = useMemo(() => {
    if (normalized.pagination?.hasServerMeta) {
      return normalized.items;
    }
    return filterItemsClient(normalized.items, filters, debouncedSearch);
  }, [normalized.items, normalized.pagination?.hasServerMeta, filters, debouncedSearch]);

  const hasServerPagination = useMemo(() => {
    const pageSize = Number(normalized.pagination?.per_page || 0);
    const total = Number(normalized.pagination?.total || 0);
    return normalized.pagination?.hasServerMeta && pageSize > 0 && total > pageSize;
  }, [normalized.pagination]);

  const rows = useMemo(() => {
    if (hasServerPagination) return normalized.items;

    const page = Number(paginationModel?.page || 0);
    const pageSize = Number(paginationModel?.pageSize || 25);
    const start = page * pageSize;
    return locallyFilteredItems.slice(start, start + pageSize);
  }, [hasServerPagination, normalized.items, locallyFilteredItems, paginationModel]);

  const rowCount = hasServerPagination
    ? Number(normalized.pagination?.total || normalized.items.length)
    : locallyFilteredItems.length;

  const computedSummary = useMemo(
    () => computeSummaryFromItems(locallyFilteredItems),
    [locallyFilteredItems]
  );

  const summary = useMemo(() => {
    const source = normalized.summary || {};
    const hasServerSummary = source?.total !== undefined && source?.total !== null;
    if (hasServerSummary) {
      return {
        ...source,
        total: Number(source?.total || computedSummary.total || 0),
        avg_score: source?.avg_score ?? computedSummary.avg_score,
        by_band: Array.isArray(source?.by_band) && source.by_band.length ? source.by_band : computedSummary.by_band,
        by_recommended_action:
          Array.isArray(source?.by_recommended_action) && source.by_recommended_action.length
            ? source.by_recommended_action
            : computedSummary.by_recommended_action,
      };
    }

    return computedSummary;
  }, [normalized.summary, computedSummary]);

  const computedKpis = useMemo(() => computeKpis(locallyFilteredItems), [locallyFilteredItems]);
  const kpis = useMemo(() => {
    return {
      total: Number(summary?.total ?? normalized.pagination?.total ?? computedKpis.total ?? 0),
      avg_score: summary?.avg_score ?? computedKpis.avg_score,
      avg_score_over_5: toRatingOver5(summary?.avg_score ?? computedKpis.avg_score),
      completed_pct: summary?.completed_pct ?? computedKpis.completed_pct,
      proposed_recommendations_count:
        summary?.proposed_recommendations_count ?? computedKpis.proposed_recommendations_count,
      approved_recommendations_count:
        summary?.approved_recommendations_count ?? computedKpis.approved_recommendations_count,
      avg_delta_vs_previous: summary?.avg_delta_vs_previous ?? computedKpis.avg_delta_vs_previous,
    };
  }, [computedKpis, normalized.pagination?.total, summary]);
  const statusDistribution = useMemo(() => {
    const byStatus = Array.isArray(summary?.by_status) ? summary.by_status : [];
    if (byStatus.length) return byStatus;
    return computeStatusDistribution(locallyFilteredItems);
  }, [summary?.by_status, locallyFilteredItems]);

  const finalizeMutation = useMutation({
    mutationFn: (payload) => finalizeAppraisalActions(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appraisals-report"] });
    },
  });

  return {
    queryParams,
    rows,
    allItems: locallyFilteredItems,
    rowCount,
    summary,
    kpis,
    statusDistribution,
    lookups: normalized.lookups,
    isLoading: reportQuery.isLoading,
    isFetching: reportQuery.isFetching,
    isError: reportQuery.isError,
    errorMessage: safeMsg(reportQuery.error, "Failed to load appraisals report"),
    refetch: reportQuery.refetch,
    finalizeActions: finalizeMutation.mutateAsync,
    finalizePending: finalizeMutation.isPending,
    finalizeError: finalizeMutation.error,
    legacySource: normalized.legacy,
  };
}
